"use client";

import React, { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { cn } from "@/lib/utils";

export interface IntegrationMilestone {
  id: string;
  title: string;
  date: string;
  description: string;
  category: string;
  status: "completed" | "in-progress" | "planned";
  icon?: React.ReactNode;
}

interface RoadmapTimelineProps {
  milestones: IntegrationMilestone[];
  className?: string;
}

export function RoadmapTimeline({ milestones, className }: RoadmapTimelineProps) {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(timelineRef, { once: false, amount: 0.2 });

  // Group milestones by category
  const groupedMilestones = milestones.reduce<Record<string, IntegrationMilestone[]>>(
    (acc, milestone) => {
      if (!acc[milestone.category]) {
        acc[milestone.category] = [];
      }
      acc[milestone.category].push(milestone);
      return acc;
    },
    {}
  );

  // Get unique categories
  const categories = Object.keys(groupedMilestones);

  // Filter milestones by active category or show all if none selected
  const filteredMilestones = activeCategory
    ? groupedMilestones[activeCategory]
    : milestones;

  const handleCategoryClick = (category: string) => {
    setActiveCategory(activeCategory === category ? null : category);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "in-progress":
        return "bg-blue-500";
      case "planned":
        return "bg-amber-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <div className={cn("w-full max-w-5xl mx-auto px-4 py-8", className)}>
      {/* Category filters */}
      <div className="flex flex-wrap gap-2 mb-8">
        {categories.map((category) => (
          <button
            key={category}
            onClick={() => handleCategoryClick(category)}
            className={cn(
              "px-4 py-2 rounded-full text-sm font-medium transition-[color,box-shadow] shadow-xs hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500",
              activeCategory === category
                ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-100"
                : "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-700"
            )}
          >
            {category}
          </button>
        ))}
      </div>

      {/* Timeline */}
      <div ref={timelineRef} className="relative">
        {/* Timeline line */}
        <div className="absolute left-4 md:left-1/2 top-0 h-full w-0.5 bg-gray-200 dark:bg-gray-700 transform -translate-x-1/2"></div>

        {/* Milestones */}
        <div className="space-y-12">
          {filteredMilestones.map((milestone, index) => (
            <motion.div
              key={milestone.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className={cn(
                "relative flex flex-col md:flex-row items-start",
                index % 2 === 0 ? "md:flex-row-reverse" : ""
              )}
            >
              {/* Timeline dot */}
              <div className="absolute left-4 md:left-1/2 w-8 h-8 rounded-full border-4 border-white dark:border-gray-900 transform -translate-x-1/2 z-10 shadow-sm transition-shadow hover:shadow-md">
                <div
                  className={cn(
                    "w-full h-full rounded-full",
                    getStatusColor(milestone.status)
                  )}
                >
                  {milestone.icon && (
                    <div className="flex items-center justify-center h-full w-full text-white">
                      {milestone.icon}
                    </div>
                  )}
                </div>
              </div>

              {/* Date */}
              <div
                className={cn(
                  "w-full md:w-[calc(50%-2rem)] text-sm font-medium text-gray-500 dark:text-gray-400 mb-2 md:mb-0",
                  index % 2 === 0 ? "md:text-left md:pl-8" : "md:text-right md:pr-8"
                )}
              >
                {milestone.date}
              </div>

              {/* Content */}
              <motion.div
                whileHover={{ y: -4 }}
                className={cn(
                  "ml-12 md:ml-0 w-full md:w-[calc(50%-2rem)]",
                  index % 2 === 0 ? "md:pr-8" : "md:pl-8"
                )}
              >
                <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm hover:shadow-md transition-[box-shadow] border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {milestone.title}
                    </h3>
                    <span
                      className={cn(
                        "px-2.5 py-0.5 rounded-full text-xs font-medium",
                        milestone.status === "completed"
                          ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-100"
                          : milestone.status === "in-progress"
                          ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100"
                          : "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-100"
                      )}
                    >
                      {milestone.status.replace("-", " ")}
                    </span>
                  </div>
                  <span className="inline-block mb-2 px-2.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-full text-xs">
                    {milestone.category}
                  </span>
                  <p className="text-gray-600 dark:text-gray-300">
                    {milestone.description}
                  </p>
                </div>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default RoadmapTimeline;
